import { html, TemplateResult } from 'lit-html';

import { originalPriceStyle, productPriceStyle } from '../components/productCards/styles';
import { TAX_OPTIONS } from '../constants';
import { ProductCardProps, ProductSetProps } from '../types/productProps';

function getProductPrice(productSetProps: ProductSetProps, productCardProps: ProductCardProps): TemplateResult {
    const productCurrencyNode =
        productSetProps._.data && productSetProps._.data.site && productSetProps._.data.site.currency;

    const formatPrice = (price: number): string => {
        const { symbol, symbolPlacement, decimalToken, decimalPlaces, thousandsToken } = productCurrencyNode.display;

        const replaceTokens = { '.': decimalToken, ',': thousandsToken };
        const formatDecimalPrice = price
            .toLocaleString('en-US', { minimumFractionDigits: decimalPlaces })
            .replace(/[.,]/g, match => replaceTokens[match]);

        return symbolPlacement === 'LEFT' ? `${symbol}${formatDecimalPrice}` : `${formatDecimalPrice}${symbol}`;
    };

    const productDisplayPrice = (price: number): string => {
        if (productCurrencyNode) {
            return formatPrice(price);
        }

        // If currencyNode is null, return intl format
        return new Intl.NumberFormat(productCardProps.displayLocale, {
            style: 'currency',
            currency: productCardProps.currencyCode,
        }).format(price);
    };

    const getProductPriceWithTax = (): TemplateResult => {
        let displayPriceWithTax = '';
        if (typeof productCardProps.priceWithTax === 'number' && productCardProps.currencyCode) {
            displayPriceWithTax = productDisplayPrice(productCardProps.priceWithTax);
        }

        let displayOriginalPriceWithTax = '';
        if (typeof productCardProps.originalPriceWithTax === 'number' && productCardProps.currencyCode) {
            displayOriginalPriceWithTax = productDisplayPrice(productCardProps.originalPriceWithTax);
        }

        return html`
            <div class="${productPriceStyle(productSetProps)}" data-test-id="product-set-widget-price">
                ${displayOriginalPriceWithTax
                    ? html`
                          <span
                              class="${originalPriceStyle(productSetProps)}"
                              data-test-id="product-set-widget-original-price"
                              >${displayOriginalPriceWithTax}</span
                          >
                      `
                    : ''}
                ${displayPriceWithTax}
            </div>
        `;
    };

    const getProductPriceWithoutTax = (): TemplateResult => {
        let displayPriceWithoutTax = '';
        if (typeof productCardProps.priceWithoutTax === 'number' && productCardProps.currencyCode) {
            displayPriceWithoutTax = productDisplayPrice(productCardProps.priceWithoutTax);
        }

        let displayOriginalPriceWithoutTax = '';
        if (typeof productCardProps.originalPriceWithoutTax === 'number' && productCardProps.currencyCode) {
            displayOriginalPriceWithoutTax = productDisplayPrice(productCardProps.originalPriceWithoutTax);
        }

        return html`
            <div class="${productPriceStyle(productSetProps)}" data-test-id="product-set-widget-price">
                ${displayOriginalPriceWithoutTax
                    ? html`
                          <span
                              class="${originalPriceStyle(productSetProps)}"
                              data-test-id="product-set-widget-original-price"
                              >${displayOriginalPriceWithoutTax}</span
                          >
                      `
                    : ''}
                ${displayPriceWithoutTax}
            </div>
        `;
    };

    const getProductPriceWithAndWithoutTax = (): TemplateResult => {
        let displayPriceWithTax = '';
        let displayOriginalPriceWithTax = '';
        if (typeof productCardProps.priceWithTax === 'number' && productCardProps.currencyCode) {
            const formatter = new Intl.NumberFormat(productCardProps.displayLocale, {
                style: 'currency',
                currency: productCardProps.currencyCode,
            });
            displayPriceWithTax = `${formatter.format(productCardProps.priceWithTax)} (Inc. Tax)`;
            if (typeof productCardProps.originalPriceWithTax === 'number') {
                displayOriginalPriceWithTax = formatter.format(productCardProps.originalPriceWithTax);
            }
        }

        let displayPriceWithoutTax = '';
        let displayOriginalPriceWithoutTax = '';
        if (typeof productCardProps.priceWithoutTax === 'number' && productCardProps.currencyCode) {
            const formatter = new Intl.NumberFormat(productCardProps.displayLocale, {
                style: 'currency',
                currency: productCardProps.currencyCode,
            });
            displayPriceWithoutTax = `${formatter.format(productCardProps.priceWithoutTax)} (Ex. Tax)`;
            if (typeof productCardProps.originalPriceWithoutTax === 'number') {
                displayOriginalPriceWithoutTax = formatter.format(productCardProps.originalPriceWithoutTax);
            }
        }

        return html`
            <div class="${productPriceStyle(productSetProps)}" data-test-id="product-set-widget-price">
                <div>
                    ${displayOriginalPriceWithTax
                        ? html`
                              <span
                                  class="${originalPriceStyle(productSetProps)}"
                                  data-test-id="product-set-widget-original-price"
                                  >${displayOriginalPriceWithTax}</span
                              >
                          `
                        : ''}
                    ${displayPriceWithTax}
                </div>
                <div>
                    ${displayOriginalPriceWithoutTax
                        ? html`
                              <span
                                  class="${originalPriceStyle(productSetProps)}"
                                  data-test-id="product-set-widget-original-price"
                                  >${displayOriginalPriceWithoutTax}</span
                              >
                          `
                        : ''}
                    ${displayPriceWithoutTax}
                </div>
            </div>
        `;
    };

    const dataNode = productSetProps._.data;
    const plpTaxSetting = dataNode && dataNode.site ? dataNode.site.settings.tax.plp : undefined;

    switch (plpTaxSetting) {
        case TAX_OPTIONS.INCLUDED:
            return getProductPriceWithTax();
        case TAX_OPTIONS.EXCLUDED:
            return getProductPriceWithoutTax();
        case TAX_OPTIONS.BOTH:
            return getProductPriceWithAndWithoutTax();
        default:
            return getProductPriceWithoutTax();
    }
}

export default getProductPrice;
